import React from 'react';
import { BookCheck, Eye, KeyRound, UserCheck } from 'lucide-react';
import SectionHeader from '@/components/site/SectionHeader';
import GoldText from '@/components/site/GoldText';
import { cn } from '@/lib/utils';

const blocks = [
  {
    icon: BookCheck,
    title: 'Approved information',
    body: 'Identify the sources the assistant uses and who is responsible for keeping them current.',
  },
  {
    icon: KeyRound,
    title: 'Defined permissions',
    body: 'Separate reading information, preparing recommendations, and executing actions.',
  },
  { icon: UserCheck, title: 'Human review', body: 'Specify approval requirements and escalation paths.' },
  {
    icon: Eye,
    title: 'Operating visibility',
    body: 'Document the activity records, usage information, and review process available within the scope.',
  },
];

const permissions: [string, 'Allowed' | 'Approval required' | 'Not permitted' | 'Escalate'][] = [
  ['Read approved service information', 'Allowed'],
  ['Prepare a response draft', 'Allowed'],
  ['Send an external message', 'Approval required'],
  ['Change payment details', 'Not permitted'],
  ['Handle an unsupported request', 'Escalate'],
];

const permissionTone: Record<string, string> = {
  Allowed: 'text-softwhite border-rule2',
  'Approval required': 'text-goldcore border-goldcore/50',
  'Not permitted': 'text-victorycoral border-victorycoral/50',
  Escalate: 'text-goldcore border-goldcore/50',
};

const Controls = () => (
  <section aria-labelledby="controls-title" className="border-t border-rule">
    <div className="section-container">
      <SectionHeader
        id="controls-title"
        eyebrow="07 / Controls"
        title={
          <>
            An assistant should know its job—<GoldText>and its limits.</GoldText>
          </>
        }
      >
        <p>
          We define what the assistant can access, what it can prepare, which actions require approval, and when a
          person takes over.
        </p>
      </SectionHeader>

      <div className="reveal mt-14 grid gap-12 lg:grid-cols-[1fr_1.05fr]">
        <ul className="grid gap-x-8 gap-y-8 sm:grid-cols-2">
          {blocks.map(({ icon: Icon, title, body }) => (
            <li key={title}>
              <Icon className="mb-4 h-5 w-5 text-goldcore" strokeWidth={1.5} aria-hidden="true" />
              <h3 className="mb-2 text-2xl">{title}</h3>
              <p className="text-sm text-mist">{body}</p>
            </li>
          ))}
        </ul>

        <figure className="surface surface-lit overflow-hidden">
          <figcaption className="border-b border-rule px-6 py-5">
            <span className="eyebrow">Example</span>
            <span className="mt-1 block font-serif text-[1.4rem] leading-tight">Permission profile</span>
          </figcaption>
          <table className="w-full text-sm">
            <caption className="sr-only">Example permission profile for a customer inquiry assistant</caption>
            <thead className="sr-only">
              <tr>
                <th scope="col">Action</th>
                <th scope="col">Permission</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rule/70">
              {permissions.map(([action, level]) => (
                <tr key={action}>
                  <th scope="row" className="px-5 py-3.5 text-left font-normal text-softwhite/90">
                    {action}
                  </th>
                  <td className="px-5 py-3.5 text-right">
                    <span
                      className={cn(
                        'inline-block whitespace-nowrap border px-2 py-0.5 font-mono text-[0.68rem] uppercase tracking-[0.1em]',
                        permissionTone[level],
                      )}
                    >
                      {level}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </figure>
      </div>

      <p className="reveal mt-10 text-mist">Controls are configured around the workflow and confirmed before launch.</p>
    </div>
  </section>
);

export default Controls;
